import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { SPRING } from "../lib/motion";
import Button from "./Button";
import ThemeToggle from "./ThemeToggle";
import GetApp from "./GetApp";
import { Close, Menu } from "./icons";
import { COMPANY } from "../data/company";
import "./nav.css";

/*
 * Named for what a gym owner is looking for, not for what the section is
 * built from.
 */
const LINKS = [
  { label: "How it works", href: "/#screens" },
  { label: "On a computer", href: "/#desk" },
  { label: "Pricing", to: "/pricing" },
  { label: "About", to: "/about" },
];

/**
 * Sign-in is a plain link to the console, not a form on this site.
 *
 * The session token lives in localStorage, which is origin-scoped, so a login
 * form here could not hand a session to app.varzishmate.com without inventing
 * a cross-origin handoff. Linking to where the token already lives costs
 * nothing, and keeps this site free of auth, secrets and any reason to appear
 * in the API's ALLOWED_ORIGINS.
 */
const CONSOLE_URL = `${COMPANY.urls.console}/login`;

/**
 * How far the page scrolls before the bar turns to glass.
 *
 * Deliberately small. The trigger is "the page has moved at all", not "the
 * hero is gone" — a bar that stays transparent over scrolling content has
 * text sitting on whatever happens to pass underneath it.
 */
const LIFT_AT = 24;

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [lifted, setLifted] = useState(false);
  const [hovered, setHovered] = useState(null);
  const reduce = useReducedMotion();
  const location = useLocation();


  // A route change must never leave the sheet hanging over the new page.
  useEffect(() => setOpen(false), [location.pathname, location.hash]);

  useEffect(() => {
    const onScroll = () => setLifted(window.scrollY > LIFT_AT);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [open]);

  const isActive = (link) => link.to && location.pathname === link.to;

  const renderLink = (link, className) => {
    const content = <span className="nav__label">{link.label}</span>;
    const props = {
      className,
      "data-active": isActive(link) || undefined,
      onMouseEnter: () => setHovered(link.label),
      onMouseLeave: () => setHovered(null),
      onFocus: () => setHovered(link.label),
      onBlur: () => setHovered(null),
    };

    const pill =
      className === "nav__link" && hovered === link.label ? (
        <motion.span
          className="nav__pill"
          layoutId="nav-pill"
          /*
           * Nav hover fires dozens of times a session, so this is deliberately
           * at the fast end. Anything slower and moving across five links
           * reads as the highlight lagging behind the cursor.
           */
          transition={
            reduce
              ? { duration: 0 }
              : { type: "spring", bounce: 0, duration: 0.22 }
          }
        />
      ) : null;

    /* `key` is written on the element, never spread — React reads it before
       the spread is applied and warns when it finds one inside. */
    return link.to ? (
      <Link key={link.label} {...props} to={link.to}>
        {pill}
        {content}
      </Link>
    ) : (
      <a key={link.label} {...props} href={link.href}>
        {pill}
        {content}
      </a>
    );
  };

  /*
   * The sheet is a sibling of <nav>, not a child of it. backdrop-filter makes
   * an element a containing block for position: fixed descendants, so a sheet
   * inside the nav resolved its inset against the 56px-tall bar instead of the
   * viewport and rendered as a sliver.
   */
  return (
    <>
      <nav
        className="nav"
        aria-label="Main"
        data-lifted={lifted}
      >
        <div className="nav__inner">
          <Link className="nav__wordmark" to="/" aria-label="Varzish Mate home">
            Varzish Mate
          </Link>

          <div className="nav__links">
            {LINKS.map((link) => renderLink(link, "nav__link"))}
          </div>

          <div className="nav__cta">
            <ThemeToggle />
            <Button
              className="nav__signin"
              href={CONSOLE_URL}
              data-signin
              variant="quiet"
              size="sm"
            >
              Sign in
            </Button>
            {/* The page's main action, always in reach on a wide screen. */}
            <GetApp size="sm" short webOnly link={false} />
          </div>

          {/*
            * Below 900px the bar keeps one thing besides the menu: the way
            * back in for a gym already on Varzish Mate. An owner who opens
            * this site on a phone is as likely to be a customer looking for
            * the console as a stranger, and making them open a sheet to find
            * "Sign in" costs every one of them a tap.
            */}
          <Button
            className="nav__signin-compact"
            href={CONSOLE_URL}
            data-signin
            variant="quiet"
            size="sm"
          >
            Sign in
          </Button>

          <button
            className="nav__toggle"
            type="button"
            aria-expanded={open}
            aria-controls="nav-sheet"
            onClick={() => setOpen((v) => !v)}
          >
            <span className="visually-hidden">Open menu</span>
            <Menu />
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            id="nav-sheet"
            className="nav__sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Site menu"
            /*
             * It leaves along the path it arrived on. A panel that enters from
             * the top and exits sideways reads as two different panels.
             */
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: -12 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -12 }}
            transition={reduce ? { duration: 0.15 } : SPRING.sheet}
          >
            <button
              className="nav__sheet-close"
              type="button"
              onClick={() => setOpen(false)}
            >
              <span className="visually-hidden">Close menu</span>
              <Close />
            </button>

            {LINKS.map((link) => renderLink(link, "nav__sheet-link"))}

            <div className="nav__sheet-actions">
              <GetApp />
              <Button href={CONSOLE_URL} data-signin variant="quiet" size="lg">
                Sign in
              </Button>
            </div>

            <div className="nav__sheet-theme">
              <span>Appearance</span>
              <ThemeToggle />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
